/**
 * B1020 — the storefront Gift cards page, laid out from 842:2 (@1440) and
 * 843:2 (@390): the hero, PICK A DESIGN (when the business has uploaded any),
 * AMOUNT (presets + CUSTOM within the business's range, refused at the field),
 * WHO IT'S FOR (their name and email, yours, NOW / ON A DATE, MESSAGE ≤200,
 * email me a copy) and the ONE door ADD TO CART at the END of the form with
 * "N already in your cart · CHECKOUT →" under it. The right column is a
 * PREVIEW only — Pathik 2026-10-23: a door on the right with the form on the
 * left is "very weird". HOW IT WORKS and THE FINE PRINT close the page. The
 * shapes come from lib/giftCardsPage.ts; this file only lays them out.
 * Built from the PNG — the frame's redline JSON is pending (Figma rate cap).
 */
import type { ReactElement, ReactNode } from "react";
import { Banner, Button, EmptyState, Input, PickerTile, SegmentedControl, Skeleton, StickyBar, Switch, Textarea, Tile } from "@transact/ui";
import {
  MESSAGE_MAX,
  amountNote,
  customHint,
  doorWords,
  saveWords,
  previewFacts,
  wholeMoney,
  type DraftIssues,
  type GiftCardDraft,
  type GiftCardOffer,
} from "../lib/giftCardsPage.js";

export type GiftCardsState =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly message: string; readonly retry: boolean }
  | { readonly kind: "off" }
  | { readonly kind: "ready"; readonly offer: GiftCardOffer };

export interface GiftCardsVenue {
  readonly businessName?: string | undefined;
  readonly logoUrl?: string | undefined;
}

/**
 * The page's bar (brand, ‹ TICKETS, CART · N). B1022 — the balance page wears it without a cart (`cartLines` absent).
 * B1020 (d.2) — `word` is 843:2's right word (GIFT CARDS): under 1024 it stands where ‹ Tickets and the cart were.
 */
export function Bar(props: { readonly venue: GiftCardsVenue; readonly ticketsUrl?: string | undefined; readonly cartLines?: number | undefined; readonly word?: string | undefined }): ReactElement {
  return (
    <header className={props.word !== undefined ? "gcards-bar gcards-bar--worded" : "gcards-bar"} data-redline="gcards-bar">
      <p className="gcards-brand">
        <span className="gcards-brand-tile" aria-hidden="true">
          {props.venue.logoUrl !== undefined ? <img src={props.venue.logoUrl} alt="" /> : null}
        </span>
        {props.venue.businessName ?? "Gift cards"}
      </p>
      {props.ticketsUrl !== undefined ? (
        <a className="gcards-back" href={props.ticketsUrl}>
          ‹ Tickets
        </a>
      ) : (
        <span />
      )}
      {props.ticketsUrl !== undefined && props.cartLines !== undefined ? (
        <a className="gcards-cart" href={props.ticketsUrl} aria-label={`Cart, ${props.cartLines} ${props.cartLines === 1 ? "item" : "items"}`}>
          {`Cart · ${props.cartLines}`}
        </a>
      ) : (
        <span />
      )}
      {props.word !== undefined ? (
        <span className="gcards-bar-word" data-redline="gcards-bar-word">
          {props.word}
        </span>
      ) : null}
    </header>
  );
}

function Card(props: { readonly title: string; readonly note?: string | undefined; readonly redline: string; readonly children: ReactNode; readonly id?: string }): ReactElement {
  const head = `${props.redline}-head`;
  return (
    <section className="gcards-card" aria-labelledby={head} data-redline={props.redline}>
      <div className="gcards-card-head">
        <h2 className="gcards-cap" id={head}>
          {props.title}
        </h2>
        {props.note !== undefined ? <p className="gcards-card-note">{props.note}</p> : null}
      </div>
      {props.children}
    </section>
  );
}

/** The card's face: the business's design, or the house face in its accent until it uploads one (B1024). */
export function CardFace(props: { readonly businessName?: string | undefined; readonly amount?: string; readonly tone: number; readonly size: "pick" | "preview" }): ReactElement {
  return (
    <span className={`gcards-face gcards-face--${props.size} gcards-face--t${props.tone % 4}`} aria-hidden="true">
      <span className="gcards-face-words">
        <span className="gcards-face-name">{props.businessName ?? ""}</span>
        <span className="gcards-face-kind">Gift card</span>
      </span>
      {props.amount !== undefined ? <span className="gcards-face-amount">{props.amount}</span> : null}
    </span>
  );
}

export interface GiftCardsFormProps {
  readonly offer: GiftCardOffer;
  readonly venue: GiftCardsVenue;
  readonly draft: GiftCardDraft;
  readonly today: string;
  readonly issues: DraftIssues;
  readonly onDraft: (next: GiftCardDraft) => void;
  readonly onAdd: () => void;
  readonly busy: boolean;
  /** The cart's sentence under the door, when it already holds cards. */
  readonly cartNote?: string | undefined;
  /** The door shut with its reason (the cart already holds the most cards an order can). */
  readonly fullWhy?: string | undefined;
  readonly checkoutUrl?: string | undefined;
  /** B1020 (2b) — the form holds a card already on the cart: the door SAVES it (and the cart's cap never shuts it). */
  readonly editing?: boolean | undefined;
  readonly banner?: { readonly tone: "success" | "danger"; readonly text: string } | undefined;
}

/**
 * What stands by the door: the cart's sentence (or why the door is shut, or the
 * editing note) and CHECKOUT →. At 1440 it is beside the door (842:2); under
 * 1024 the door floats in its bar, so 843:2 draws it AFTER the preview — the
 * same words rendered there (`placed="after"`), one face showing at a time.
 */
function DoorNote(props: { readonly p: GiftCardsFormProps; readonly shut: string | undefined; readonly placed: "door" | "after" }): ReactElement | null {
  const { p, shut } = props;
  const words =
    p.editing === true ? "You’re changing a card already in your cart — saving keeps its place." : shut !== undefined ? shut : p.cartNote;
  const checkout = (p.editing === true || p.cartNote !== undefined) && p.checkoutUrl !== undefined;
  if (props.placed === "after" && words === undefined && !checkout) return null;
  return (
    <div className={props.placed === "door" ? "gcards-door-note" : "gcards-next"} {...(props.placed === "after" ? { "data-redline": "gcards-next" } : {})}>
      {words !== undefined ? <p {...(props.placed === "door" && p.editing !== true && shut !== undefined ? { id: "gcards-full" } : {})}>{words}</p> : null}
      {checkout ? (
        <a className="gcards-checkout" href={p.checkoutUrl}>
          Checkout →
        </a>
      ) : null}
    </div>
  );
}

function Form(p: GiftCardsFormProps): ReactElement {
  const { offer, draft } = p;
  const set = (patch: Partial<GiftCardDraft>): void => p.onDraft({ ...draft, ...patch });
  const preview = previewFacts(draft, offer, p.today);
  /* editing a card that is already counted: the cart's cap is not this door's */
  const shut = p.editing === true ? undefined : p.fullWhy;
  const designs = offer.designs;
  const messageMax = MESSAGE_MAX;
  return (
    <div className="gcards-grid">
      <form
        className="gcards-form"
        noValidate
        aria-label="Gift card"
        onSubmit={(e) => {
          e.preventDefault();
          p.onAdd();
        }}
      >
        {designs.length > 0 ? (
          <Card title="Pick a design" note={`${designs.length === 1 ? "One design" : `${designs.length} designs`} on offer — the one you pick is on the email and the card page.`} redline="gcards-designs">
            <div className="gcards-designs" role="group" aria-label="Design">
              {designs.map((d, i) => (
                <Tile key={d.designId} title={d.name} pressed={draft.designId === d.designId} onPress={() => set({ designId: d.designId })}>
                  <CardFace businessName={p.venue.businessName} tone={i} size="pick" />
                </Tile>
              ))}
            </div>
          </Card>
        ) : null}

        <Card title="Amount" note={amountNote(offer, p.venue.businessName)} redline="gcards-amount">
          <div className="gcards-amounts" role="group" aria-label="Amount">
            {offer.presets.map((a) => (
              <PickerTile
                key={a}
                compact
                value={wholeMoney(a, offer.currency)}
                pressed={draft.pick.kind === "preset" && draft.pick.amount === a}
                onPress={() => set({ pick: { kind: "preset", amount: a } })}
              />
            ))}
            <PickerTile compact value="Custom" pressed={draft.pick.kind === "custom"} onPress={() => set({ pick: { kind: "custom" } })} />
          </div>
          <div className="gcards-custom">
            <Input
              id="gcards-custom"
              width="full"
              label="Custom amount"
              value={draft.customText}
              inputMode="numeric"
              placeholder={wholeMoney(0, offer.currency).replace(/0/g, "")}
              hint={customHint(offer)}
              error={p.issues.amount}
              onChange={(v) => set({ customText: v, pick: { kind: "custom" } })}
            />
            <p className="gcards-aside">{`The amount is loaded in full — no fees, and ${p.venue.businessName ?? "we"} ${p.venue.businessName !== undefined ? "keeps" : "keep"} nothing until the card is used.`}</p>
          </div>
        </Card>

        <Card title="Who it's for" redline="gcards-who">
          <div className="gcards-pair">
            <Input id="gcards-to-name" required label="Their name" value={draft.recipientName} error={p.issues.recipientName} onChange={(v) => set({ recipientName: v })} />
            <Input
              id="gcards-to-email"
              required
              label="Their email"
              type="email"
              value={draft.recipientEmail}
              hint="The card goes to this address. Check it twice — a typo sends it to a stranger."
              error={p.issues.recipientEmail}
              onChange={(v) => set({ recipientEmail: v })}
            />
          </div>
          <div className="gcards-pair">
            <Input id="gcards-from-name" required label="Your name" value={draft.senderName} hint="Shown as the sender on the email." error={p.issues.senderName} onChange={(v) => set({ senderName: v })} />
            <div className="gcards-send">
              <div className="gcards-send-row">
                <SegmentedControl
                  legend="Send"
                  legendVisible
                  name="gcards-send"
                  railRedline="gcards-send-rail"
                  options={[
                    { value: "now", label: "Now" },
                    { value: "date", label: "On a date" },
                  ]}
                  value={draft.send}
                  onChange={(v) => set({ send: v === "date" ? "date" : "now" })}
                />
                {draft.send === "date" ? (
                  /* width-ok: 842:2 draws the date 160 wide, ten beside SEND's rail — .gcards-send-row owns it */
                  <Input id="gcards-send-date" required label="Send on" type="date" value={draft.sendDate} error={p.issues.sendDate} onChange={(v) => set({ sendDate: v })} />
                ) : null}
              </div>
              <p className="gcards-hint">
                {draft.send === "date"
                  ? "Arrives at 9:00 on that day. Change or cancel it from Your orders until it sends."
                  : "Arrives a minute after you pay."}
              </p>
            </div>
          </div>
          <Textarea
            id="gcards-message"
            label="Message"
            rows={3}
            value={draft.message}
            hint={`Optional · up to ${messageMax} characters · printed on the email and the card page.`}
            {...(p.issues.message !== undefined ? { error: p.issues.message } : {})}
            onChange={(v) => set({ message: v })}
          />
          <Switch id="gcards-copy" label="Email me a copy of the card" checked={draft.copyToMe} onChange={(v) => set({ copyToMe: v })} />
          <div className="gcards-door" data-redline="gcards-door">
            {p.banner !== undefined ? <Banner tone={p.banner.tone}>{p.banner.text}</Banner> : null}
            <div className="gcards-door-row">
              {/* B1020 (d.2) — 843:2 floats the ONE door in the package's frosted bar under 1024; seated, it is 842:2's 280 */}
              <StickyBar seatedWhenWide redline="gcards-stickybar">
                <Button type="submit" variant="primary" busy={p.busy} disabled={shut !== undefined} {...(shut !== undefined ? { describedBy: "gcards-full" } : {})} onClick={() => undefined /* dead-ok: a submit button — the form's onSubmit is the act */}>
                  {p.editing === true ? saveWords(draft, offer) : doorWords(draft, offer)}
                </Button>
              </StickyBar>
              <DoorNote p={p} shut={shut} placed="door" />
            </div>
          </div>
        </Card>
      </form>

      <aside className="gcards-preview" aria-labelledby="gcards-preview-head" data-redline="gcards-preview">
        <h2 className="gcards-cap" id="gcards-preview-head">
          {preview.head}
        </h2>
        <CardFace businessName={p.venue.businessName} amount={preview.amount} tone={Math.max(0, designs.findIndex((d) => d.designId === draft.designId))} size="preview" />
        <dl className="gcards-facts">
          <div className="gcards-fact">
            <dt>{preview.to}</dt>
            <dd>{preview.from ?? ""}</dd>
          </div>
          <div className="gcards-fact">
            <dt>Sends by email</dt>
            <dd>{preview.when}</dd>
          </div>
          <div className="gcards-fact gcards-fact--total">
            <dt>Gift card</dt>
            <dd>{preview.total}</dd>
          </div>
        </dl>
        <p className="gcards-aside" data-redline="gcards-preview-note">
          {preview.note}
        </p>
      </aside>
      <DoorNote p={p} shut={shut} placed="after" />
    </div>
  );
}

function Closing(props: { readonly businessName?: string | undefined }): ReactElement {
  const at = props.businessName !== undefined ? ` at ${props.businessName}` : "";
  return (
    <div className="gcards-closing">
      <section className="gcards-card gcards-how" aria-labelledby="gcards-how" data-redline="gcards-how">
        <h2 className="gcards-cap" id="gcards-how">
          How it works
        </h2>
        <ol className="gcards-steps">
          <li>Pick a design and an amount, then tell us who it's for.</li>
          <li>We email the card on the day you choose — with a number, a PIN and your message.</li>
          <li>{`They spend it online${at} — on one order or many. What's left stays on the card.`}</li>
        </ol>
      </section>
      <section className="gcards-card" aria-labelledby="gcards-print" data-redline="gcards-print">
        <h2 className="gcards-cap" id="gcards-print">
          The fine print
        </h2>
        <ul className="gcards-print">
          <li>Never expires and carries no fees.</li>
          <li>{`Good for anything sold online${at} — not for cash.`}</li>
          <li>Several cards can pay for one order; a bank card covers the rest.</li>
          <li>A refund of an order paid by gift card goes back onto the card.</li>
          <li>Lost the email? The giver can resend it from Your orders.</li>
        </ul>
      </section>
    </div>
  );
}

export function GiftCardsPage(props: {
  readonly venue: GiftCardsVenue;
  readonly state: GiftCardsState;
  readonly ticketsUrl?: string | undefined;
  /** B1020 (d) — CHECK A BALANCE → (842:2's hero); absent when the business is not known. */
  readonly balanceUrl?: string | undefined;
  readonly cartLines: number;
  readonly onRetry: () => void;
  /** The form's props when the state is ready. */
  readonly form?: Omit<GiftCardsFormProps, "offer" | "venue"> | undefined;
}): ReactElement {
  const s = props.state;
  return (
    <main className="gcards" aria-label="Gift cards">
      <Bar venue={props.venue} ticketsUrl={props.ticketsUrl} cartLines={props.cartLines} word="Gift cards" />
      <div className="gcards-body">
        <div className="gcards-hero">
          <div className="gcards-hero-words">
            <p className="gcards-kicker">Gift cards</p>
            <h1 className="gcards-title">{props.venue.businessName !== undefined ? `Give a day at ${props.venue.businessName}` : "Give a gift card"}</h1>
            <p className="gcards-sub">
              An e-gift card for anything sold online here — emailed on the day you choose.
              {/* 843:2 closes the sentence short on the phone */}
              <span className="gcards-wide"> It never expires, and whoever holds it can check the balance any time.</span>
              <span className="gcards-narrow"> Never expires.</span>
            </p>
          </div>
          {props.balanceUrl !== undefined ? (
            <div className="gcards-hero-balance" data-redline="gcards-balance-col">
              <a className="gcards-balance" href={props.balanceUrl} data-redline="gcards-balance">
                Check a balance →
              </a>
              <p className="gcards-balance-note" data-redline="gcards-balance-note">
                {"Already have a card? See what's left on it."}
              </p>
            </div>
          ) : null}
        </div>
        {s.kind === "loading" ? (
          <Skeleton lines={6} />
        ) : s.kind === "error" ? (
          <Banner tone="danger">
            {s.message}{" "}
            {s.retry ? (
              <Button variant="neutral" onClick={props.onRetry}>
                Try again
              </Button>
            ) : null}
          </Banner>
        ) : s.kind === "off" ? (
          <EmptyState
            title="Gift cards aren't on sale here yet"
            body="This business hasn't turned gift cards on. Its tickets are one step away."
            {...(props.ticketsUrl !== undefined ? { action: <Button variant="primary" href={props.ticketsUrl} onClick={() => undefined /* dead-ok: a link — the href is the act */}>See tickets</Button> } : {})}
          />
        ) : props.form !== undefined ? (
          <>
            <Form offer={s.offer} venue={props.venue} {...props.form} />
            <Closing businessName={props.venue.businessName} />
          </>
        ) : null}
      </div>
    </main>
  );
}
